import React, { useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '../utils/focusTrap';
import styles from './ConfirmDialog.module.css';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  requireConfirmation?: boolean;
  confirmationPhrase?: string;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  requireConfirmation = false,
  confirmationPhrase = 'I confirm',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [userInput, setUserInput] = useState('');

  // Enable focus trap
  useFocusTrap(modalRef, isOpen);

  // Reset input when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setUserInput('');
    }
  }, [isOpen]);

  // Store onCancel in a ref to avoid re-running effect when callback changes
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Auto-focus input or cancel button when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (requireConfirmation) {
        // Focus input field for confirmation
        inputRef.current?.focus();
      } else {
        // Focus cancel button (safer default for destructive actions)
        cancelButtonRef.current?.focus();
      }
    }
  }, [isOpen, requireConfirmation]);

  // Handle keyboard events separately to avoid focus issues
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancelRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const isConfirmEnabled = !requireConfirmation || userInput === confirmationPhrase;

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="confirm-dialog-title" className={styles.modalTitle}>{title}</h2>
        <div id="confirm-dialog-message" className={styles.modalMessage}>{message}</div>

        {requireConfirmation && (
          <div className={styles.confirmationInput}>
            <label htmlFor="confirmation-input" className={styles.confirmationLabel}>
              Type <strong>{confirmationPhrase}</strong> to confirm:
            </label>
            <input
              ref={inputRef}
              id="confirmation-input"
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              className={styles.input}
              placeholder={confirmationPhrase}
              autoComplete="off"
            />
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button
            ref={cancelButtonRef}
            onClick={onCancel}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={!isConfirmEnabled}
            className={`${styles.button} ${styles.buttonDanger}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
